/**
 * In-browser preview for cabinet HTML / PDF / images.
 * Issued Form 12A / S-RaD / Defect use loadIssuedPaperworkPreview (same backend loader as Floor).
 */
export function detectPreviewKind(file, mime = '') {
  const m = String(mime || file?.mimeType || file?.mime || '');
  const name = String(file?.name || file?.original_name || '');
  if (m.startsWith('image/')) return 'image';
  if (m === 'application/pdf' || /\.pdf$/i.test(name)) return 'pdf';
  if (m.includes('html') || /\.html?$/i.test(name)) return 'html';
  return 'other';
}

/**
 * Load file for modal preview. HTML uses srcdoc (reliable for Form 12A issued docs).
 */
export async function loadDocumentPreview(api, basePath, fileId, fileMeta = {}) {
  const res = await api.get(`${basePath}/${fileId}/view`, { responseType: 'blob' });
  const mime = res.data.type || fileMeta.mimeType || '';
  const kind = detectPreviewKind(fileMeta, mime);

  if (kind === 'html') {
    const html = await res.data.text();
    return { kind, html, mime };
  }

  const blobUrl = URL.createObjectURL(res.data);
  return { kind, blobUrl, mime };
}

/**
 * Canonical issued paperwork preview — same backend loadIssuedPaperworkHtml as Floor.
 * Prefer recordId (exact form), then buildingId+ssCode (ACTIVE), then document cabinet.
 */
export async function loadIssuedPaperworkPreview(
  api,
  { buildingId, ssCode, recordId, documentId } = {}
) {
  if (recordId) {
    const res = await api.get(`/paperwork/issued-forms/${recordId}/preview`, {
      responseType: 'text',
      transformResponse: [(d) => d],
    });
    const html = typeof res.data === 'string' ? res.data : String(res.data || '');
    if (!html) throw new Error('No preview HTML');
    return { kind: 'html', html, mime: 'text/html' };
  }

  if (buildingId && ssCode) {
    const res = await api.get(
      `/buildings/${encodeURIComponent(buildingId)}/paperwork/${encodeURIComponent(ssCode)}/issued-preview`
    );
    const d = res.data?.data || res.data || {};
    if (!d.html) throw new Error(res.data?.error || 'No preview HTML');
    return {
      kind: 'html',
      html: d.html,
      mime: 'text/html',
      meta: d,
    };
  }

  if (buildingId && documentId) {
    return loadDocumentPreview(api, `/buildings/${buildingId}/documents`, documentId, {
      mimeType: 'text/html',
      name: 'issued.html',
    });
  }

  throw new Error('Need recordId, buildingId+ssCode, or buildingId+documentId');
}

export function releasePreview(preview) {
  if (preview?.blobUrl) URL.revokeObjectURL(preview.blobUrl);
}